export const ROADMAP_TYPES = Object.freeze({
    TIMELINE: 'TIMELINE',
    COLUMN: 'COLUMNS',
});

export const ACTION_TYPES = Object.freeze({
    NONE: 'none',
    RESIZE_LEFT: 'resize_left',
    RESIZE_RIGHT: 'resize_right',
    DRAG: 'drag',
    RESOLVE: 'resolve',
    PROMOTE_CHILD_SOURCE: 'promote_child_source',
    PROMOTE_CHILD_TARGET: 'promote_child_target',
    PROMOTE_PARENT: 'promote_parent',
    NEST: 'nest',
    FORBIDDEN: 'forbidden',
    OUT_OF_BOUNDS: 'out_of_bounds',
    CREATE_ITEM: 'create_item',
    MOVE_TO_BACKLOG: 'move_to_backlog',
});

export const SPECIAL_ACTION_IDS = Object.freeze({
    PLACEHOLDER_ITEM: 'placeholder_item',
    PROMOTE_PARENT: 'promote_parent',
    CREATE_COLUMN: 'create_column',
    CREATE_SWIMLANE: 'create_swimlane',
    ADD_IDEA: 'add_idea',
});

export const DEFAULT_PENDO_NOTIFICATION_DURATION = 3000;

export const DEFAULT_ZOOM_INDEX = 1;

export const ZOOM_LEVELS = [4, 6, 12, 18];

export const ROADMAP_GENERIC_ERROR_NOTIFICATION = Object.freeze({
    type: 'error',
    title: 'Something went wrong!',
    message: 'Please refresh',
    duration: DEFAULT_PENDO_NOTIFICATION_DURATION,
});

export const TIMELINE_ITEM_HEIGHT = 36;

export const ITEM_MIN_DURATION_IN_DAYS = 7;

export const ITEM_LOCATION_TYPES = Object.freeze({
    BACKLOG: {
        key: 'BACKLOG',
        label: 'Backlog',
        level: 0,
        icon: { symbol: 'gallery-vertical-end', backgroundColor: 'var(--palette-gray-100)', strokeColor: 'var(--palette-gray-0)' },
    },
    SWIMLANE: {
        key: 'SWIMLANE',
        label: 'Swimlane',
        level: 0,
    },
    INITIATIVE: {
        key: 'INITIATIVE',
        capitalLabel: 'Initiative',
        label: 'initiative',
        level: 1,
        icon: { symbol: 'rocket', backgroundColor: 'var(--palette-magenta-80)', strokeColor: 'var(--palette-gray-0)' },
    },
    FEATURE: {
        key: 'FEATURE',
        capitalLabel: 'Feature',
        label: 'feature',
        level: 2,
        icon: { symbol: 'star', backgroundColor: 'var(--palette-green-80)', strokeColor: 'var(--palette-gray-0)' },
    },
    IDEA: {
        key: 'IDEA',
        capitalLabel: 'Idea',
        label: 'idea',
        level: 2,
        icon: { symbol: 'lightbulb', backgroundColor: 'var(--palette-purple-80)', strokeColor: 'var(--palette-gray-0)' },
    },
    IDEA_BACKLOG: {
        key: 'IDEA_BACKLOG',
        capitalLabel: 'Idea',
        label: 'idea',
        level: 2,
        icon: { symbol: 'lightbulb', backgroundColor: 'var(--palette-purple-80)', strokeColor: 'var(--palette-gray-0)' },
    },
});

export const ITEM_TOP_LEVEL = ITEM_LOCATION_TYPES.INITIATIVE.level;

export const TIMELINE_ROW_SPACING = 8;

export const TIMELINE_CHILD_ROW_SPACING = 8;

export const TIMELINE_CHILD_ITEM_HEIGHT = TIMELINE_ITEM_HEIGHT;

export const TIMELINE_ROW_HEIGHT = TIMELINE_ITEM_HEIGHT + TIMELINE_ROW_SPACING;

export const TIMELINE_CONTAINER_PADDING = 16;

export const MIN_SWIMLANE_ROWS = 2;

export const SWIMLANE_GUTTER_WIDTH = 32;

export const BORDER_SIZE = 2;

export const ROADMAP_HEADER_MIN_HEIGHT = 150;

export const TIME_PARSE_FORMAT = 'YYYY-MM-DD';

export const TIME_PERIOD = Object.freeze({
    YEAR: 'year',
    QUARTER: 'quarter',
    MONTH: 'month',
    WEEK: 'week',
});

export const TIME_PERIOD_FORMAT = Object.freeze({
    [TIME_PERIOD.YEAR]: 'YYYY',
    [TIME_PERIOD.QUARTER]: '[Q]Q',
    [TIME_PERIOD.MONTH]: 'MMM',
    [TIME_PERIOD.WEEK]: '[Week ]WW YYYY',
});

export const TRANSITION_DURATION_MS = 200;

// used for the pendo-color-picker-presets. Will need to update to accept css colors
// https://pendo-io.atlassian.net/browse/APP-133850
/* eslint-disable internal-rules/no-hardcoded-colors */
export const SWIMLANE_COLORS = Object.freeze({
    PURPLE: '#7423E2',
    MAGENTA: '#A11A95',
    PINK: '#DA345E',
    ORANGE: '#FF9237',
    YELLOW: '#E6AF2D',
    GREEN: '#009855',
    BLUE: '#07699B',
    TEAL: '#128297',
    BLACK: '#3A3C45',
});
/* eslint-enable internal-rules/no-hardcoded-colors */

export const BACKLOG_COLOR = 'var(--palette-gray-0)';

export const ROADMAP_FIELD_VALIDATION_LIMITS = Object.freeze({
    roadmap: {
        title: {
            min: 1,
            max: 175,
        },
        description: {
            max: 175,
        },
    },
    swimlane: {
        title: {
            min: 1,
            max: 175,
        },
        description: {
            max: 175,
        },
    },
    item: {
        title: {
            min: 1,
            max: 255,
        },
        description: {
            max: 5000,
        },
    },
});

export const ROADMAP_REQUEST_FIELDS = Object.freeze({
    roadmap: ['id', 'title', 'description', 'quarterOneStartingMonth'],
    swimlane: ['id', 'title', 'description', 'color', 'action'],
    item: [
        'id',
        'title',
        'description',
        'statusId',
        'type',
        'parentId',
        'parentType',
        'row',
        'startDate',
        'endDate',
        'onBacklog',
        'columnId',
        'importedItemId',
    ],
    timelineItemCreate: [
        'title',
        'description',
        'type',
        'parentId',
        'parentType',
        'row',
        'startDate',
        'endDate',
        'onBacklog',
        'importedItemId',
    ],
    timelineItemLocationsUpdate: ['id', 'parentId', 'parentType', 'row', 'startDate', 'endDate', 'onBacklog'],
    column: ['id', 'title', 'description'],
    columnItemCreate: [
        'title',
        'description',
        'type',
        'parentId',
        'parentType',
        'columnId',
        'row',
        'onBacklog',
        'importedItemId',
    ],
    columnItemLocationsUpdate: ['id', 'parentId', 'parentType', 'columnId', 'row', 'onBacklog'],
});

export const ELEMENT_SELECTORS = Object.freeze({
    COLUMN_CHILD_ROW: 'selector__column-swimlane-item-container-child-row',
    COLUMN_ROW: 'selector__column-swimlane-item-container-row',
    COLUMN_SWIMLANE_CHILD_ITEM: 'selector__column-swimlane-child-item',
    COLUMN_SWIMLANE_ITEM_CONTAINER: 'selector__column-swimlane-item-container',
    DOWNLOAD_SCROLL: 'selector__download-scroll',
    SWIMLANE_ITEM: 'selector__swimlane-item',
});

export const BLADE_TYPES = Object.freeze({
    ROADMAP_BACKLOG: 'ROADMAP_BACKLOG',
    IDEAS_BACKLOG: 'IDEAS_BACKLOG',
    CREATE_ITEM: 'CREATE_ITEM',
    INFO_ITEM: 'INFO_ITEM',
});

// ///////////////////////////
// COLUMN ROADMAPS CONSTANTS//
// ///////////////////////////

export const MIN_COLUMN_WIDTH = 350;

export const COLUMN_ITEM_HEIGHT = 96;

export const COLUMN_CHILD_ITEM_HEIGHT = 48;

export const COLUMN_CHILD_ROW_SPACING = 5;

export const COLUMN_ROW_SPACING = 16;

export const COLUMN_ROW_HEIGHT = COLUMN_ITEM_HEIGHT + COLUMN_ROW_SPACING;

export const COLUMN_GAP = SWIMLANE_GUTTER_WIDTH / 2;

export const COLUMN_CONTAINER_PADDING = 12;
